/**
 * Escalating credit warning, rendered directly above the Velo composer.
 *
 * The gap this closes: the only forewarning used to be an email at 80% of the
 * cap, sent to OWNER/ADMIN only — so an EDITOR could spend a whole session and
 * discover the wall by being refused mid-conversation. This strip sits in the
 * one place the user is actually looking while spending.
 *
 * Escalation follows useAiCreditsStatus's rungs. `low` may be dismissed, but
 * the dismissal is scoped to the RUNG (stored as an ordinal, per session):
 * waving away "getting low" re-arms itself automatically at `critical`, and a
 * counter that reaches zero inside a single session must never be silenced for
 * a whole day the way a banner about a date two weeks out can be.
 *
 * At `empty` this renders only as a PRE-flight warning. Once the server has
 * actually refused a turn, the transcript already carries the full
 * out-of-credits message with its recovery actions, and repeating it here
 * would just say the same thing twice in one panel.
 */
import { useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { IconInfoCircle, IconX } from "@tabler/icons-react";
import { AiQuotaActions } from "@/components/AiAssistant/AiQuotaActions";
import type { AiCreditsRung, AiCreditsStatus } from "@/hooks/useAiCreditsStatus";

const DISMISS_KEY = "aiCreditsRungDismissed";

const ORDINAL: Record<AiCreditsRung, number> = {
  ok: 0,
  low: 1,
  critical: 2,
  empty: 3,
};

function readDismissed(): number {
  try {
    return Number(sessionStorage.getItem(DISMISS_KEY)) || 0;
  } catch {
    // Private-mode / blocked storage: never let the warning break the panel.
    return 0;
  }
}

/**
 * The dismissal is shared, not per-instance.
 *
 * Up to two strips can be on screen at once — the drawer sits BESIDE a wizard
 * panel by design (see the `--ai-rail-push` handling in AiAssistantDrawer), and
 * both mount their own notice. Seeding React state from sessionStorage once per
 * mount meant dismissing one left its twin standing, because the sibling never
 * re-read the key. A tiny external store keeps every mounted instance on the
 * same value.
 */
const listeners = new Set<() => void>();
let dismissedSnapshot = readDismissed();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setDismissed(next: number): void {
  dismissedSnapshot = next;
  try {
    sessionStorage.setItem(DISMISS_KEY, String(next));
  } catch {
    // Storage unavailable — the dismissal just doesn't outlive this session.
  }
  listeners.forEach((fn) => fn());
}

/** Test seam: reset the shared dismissal between cases. */
export function __resetCreditsDismissal(): void {
  dismissedSnapshot = 0;
  listeners.forEach((fn) => fn());
}

export function AiCreditsNotice({ status }: { status: AiCreditsStatus }) {
  const t = useTranslations("aiAssistant");
  const dismissed = useSyncExternalStore(subscribe, () => dismissedSnapshot);

  // The live region is mounted ALWAYS and only its CONTENT is conditional:
  // a role="status" node inserted together with its text is announced
  // inconsistently across screen readers, which would defeat the whole point
  // of a strip that is supposed to speak up on its own.
  const silent =
    !status.loaded ||
    status.rung === "ok" ||
    // Post-rejection the transcript owns the message (see file header).
    (status.rung === "empty" && status.rejected) ||
    ORDINAL[status.rung] <= dismissed;

  if (silent) return <div role="status" aria-live="polite" className="sr-only" />;

  const escalated = status.rung !== "low";
  const tone = escalated ? "hint-orange-bg hint-orange-outline" : "surface-blue outline-blue";
  const toneFont = escalated ? "hint-orange-font" : "font-blue";

  return (
    <div
      role="status"
      data-testid="ai-credits-notice"
      data-rung={status.rung}
      className={`shrink-0 flex items-start gap-2 px-3.5 py-2 border-t outline-quaternary ${tone}`}
    >
      <IconInfoCircle size={16} className={`shrink-0 mt-0.5 ${toneFont}`} />
      <div className="flex-1 min-w-0">
        <p className={`body-3-medium ${toneFont}`}>
          {status.rung === "empty"
            ? t("creditsExhausted")
            : t("creditsLow", { count: status.remaining })}
        </p>
        {/* Body states the SITUATION only. What to do about it is role
            dependent and lives in AiQuotaActions, which already knows the
            role — keeping the query out of here means this strip still
            mounts in any harness. */}
        <p className="body-3-regular font-tertiary">
          {status.rung === "empty" ? t("creditsExhaustedInfo") : t("creditsLowInfo")}
        </p>
        {/* Separate node, never concatenated: "renews on the 3rd" and "does
            not renew at all" are the two different futures a user needs to
            act on, and the second one is the whole reason a FREE user should
            stop waiting and buy. */}
        {status.resetLine && (
          <p className="body-3-regular font-tertiary">{status.resetLine}</p>
        )}
        <AiQuotaActions />
      </div>
      {!escalated && (
        <button
          type="button"
          aria-label={t("creditsDismiss")}
          onClick={() => setDismissed(ORDINAL[status.rung])}
          className="shrink-0 rounded-md p-0.5 font-tertiary hover:font-primary hover:surface-primary transition-colors cursor-pointer"
        >
          <IconX size={14} />
        </button>
      )}
    </div>
  );
}
